import { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '../ui/input'
import { cn } from '../../lib/utils'
import { normalizeResourceLabel } from './normalize'
import { LabelPill } from './LabelPill'

export interface LabelCatalogMatch {
  label: string
  count: number
}

export interface LabelEditorProps {
  labels: readonly string[]
  onChange: (next: string[]) => void | Promise<void>
  /** Catalog entries for autocomplete ("Used on other configs"). */
  catalog?: readonly LabelCatalogMatch[]
  placeholder?: string
  className?: string
  disabled?: boolean
}

function sameLabels(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((t, i) => t === b[i])
}

/**
 * Free-form label editor: pills + dashed input with Enter-to-add and
 * catalog suggestions filtered by the draft.
 *
 * Keeps an optimistic local copy so Enter immediately shows a pill while
 * the parent PATCH is in flight. Rapid edits coalesce to the latest list and
 * flush serially (ReplaceAll semantics); failures roll back to the last
 * successfully persisted baseline — never a stale in-flight snapshot.
 */
export function LabelEditor({
  labels,
  onChange,
  catalog = [],
  placeholder = 'Add or find a label…',
  className,
  disabled = false
}: LabelEditorProps) {
  const [draft, setDraft] = useState('')
  const [localLabels, setLocalLabels] = useState<string[]>(() => [...labels])
  const baselineRef = useRef<string[]>([...labels])
  const desiredRef = useRef<string[] | null>(null)
  const flushingRef = useRef(false)

  useEffect(() => {
    baselineRef.current = [...labels]
    setLocalLabels((prev) => (sameLabels(prev, labels) ? prev : [...labels]))
  }, [labels])

  const matches = useMemo(() => {
    const qRaw = draft.trim().toLowerCase()
    if (qRaw.length === 0) return []
    // Stored names are slugs ("line-12"); drafts are free-form ("Line 12").
    const qSlug = normalizeResourceLabel(draft)
    const qCompact = qRaw.replace(/[^a-z0-9]/g, '')
    const owned = new Set(localLabels)
    return catalog
      .filter((m) => {
        if (owned.has(m.label)) return false
        if (qSlug && m.label.includes(qSlug)) return true
        if (m.label.includes(qRaw)) return true
        if (qCompact && m.label.replace(/-/g, '').includes(qCompact)) return true
        return false
      })
      .slice(0, 8)
  }, [catalog, draft, localLabels])

  const flush = async () => {
    if (flushingRef.current) return
    flushingRef.current = true
    try {
      while (desiredRef.current) {
        const next = desiredRef.current
        desiredRef.current = null
        try {
          await onChange(next)
          baselineRef.current = next
        } catch {
          setLocalLabels([...baselineRef.current])
          desiredRef.current = null
          break
        }
      }
    } finally {
      flushingRef.current = false
      if (desiredRef.current) void flush()
    }
  }

  const commit = (next: string[]) => {
    setLocalLabels(next)
    setDraft('')
    desiredRef.current = next
    void flush()
  }

  const add = (raw: string) => {
    const clean = normalizeResourceLabel(raw)
    if (!clean || localLabels.includes(clean)) {
      setDraft('')
      return
    }
    commit([...localLabels, clean])
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div>
        <h2 className="m-0 text-sm font-semibold text-ink">Labels</h2>
        <p className="mt-0.5 text-xs text-ink-4">
          Free-form labels used to filter configs and the insights they produce.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {localLabels.map((label) => (
          <LabelPill
            key={label}
            label={label}
            onRemove={disabled ? undefined : () => commit(localLabels.filter((t) => t !== label))}
          />
        ))}
        <span className="relative inline-flex">
          <Input
            type="text"
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              e.stopPropagation()
              add(draft)
            }}
            placeholder={placeholder}
            aria-label="Add a label"
            className="h-6 w-37.5 rounded-full border-dashed px-2.5 text-12 shadow-none"
          />
          {matches.length > 0 && (
            <span className="absolute top-7 left-0 z-30 flex min-w-45 flex-col rounded-lg border border-line bg-bg-elev p-1 shadow-md">
              <span className="px-2 pt-0.5 pb-1 text-10 font-semibold tracking-wider text-ink-4 uppercase">
                Used on other configs
              </span>
              {matches.map((m) => (
                <button
                  key={m.label}
                  type="button"
                  onClick={() => add(m.label)}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 text-left text-xs text-ink hover:bg-bg-sunk"
                >
                  <span className="whitespace-nowrap">{m.label}</span>
                  <span className="tabular-nums text-11 text-ink-4">
                    {m.count} {m.count === 1 ? 'config' : 'configs'}
                  </span>
                </button>
              ))}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}
